import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";

const MODULO = "AsociarJefes";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const RegistrarEnteVsJefe = () => {
  const { id, idasociado } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [jefes, setJefes] = useState([]);
  const [jefeSeleccionado, setJefeSeleccionado] = useState(null);
  const [estadoAsignacion, setEstadoAsignacion] = useState("");

  const isEdit = useMemo(() => Boolean(idasociado), [idasociado]);

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const getModuloAsociarJefes = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloAsociarJefes = useMemo(() => getModuloAsociarJefes(), []);

  const puedeLeer =
    moduloAsociarJefes?.puedeLeer === true ||
    moduloAsociarJefes?.leer === true ||
    moduloAsociarJefes?.ver === true ||
    moduloAsociarJefes?.listar === true ||
    moduloAsociarJefes?.read === true ||
    moduloAsociarJefes?.view === true;

  const puedeCrear =
    moduloAsociarJefes?.puedeCrear === true ||
    moduloAsociarJefes?.crear === true ||
    moduloAsociarJefes?.create === true;

  const puedeActualizar =
    moduloAsociarJefes?.puedeActualizar === true ||
    moduloAsociarJefes?.actualizar === true ||
    moduloAsociarJefes?.editar === true ||
    moduloAsociarJefes?.update === true;

  const puedeGuardarFormulario = isEdit ? puedeActualizar : puedeCrear;

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const cleanData = useCallback(() => {
    setJefeSeleccionado(null);
    setEstadoAsignacion("");
  }, []);

  const selectStyles = useMemo(
    () => ({
      container: (base) => ({ ...base, width: "100%" }),
      control: (base, state) => ({
        ...base,
        minHeight: 38,
        borderColor: state.isFocused ? "#86b7fe" : base.borderColor,
        boxShadow: state.isFocused
          ? "0 0 0 .25rem rgba(13,110,253,.25)"
          : base.boxShadow,
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
    }),
    []
  );

  const cargarJefes = useCallback(async () => {
    if (!puedeLeer) {
      setJefes([]);
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.get("/api/jefe/listall", authConfig());

      const lista = Array.isArray(data?.jefes)
        ? data.jefes
        : Array.isArray(data?.jefesInmediatos)
        ? data.jefesInmediatos
        : Array.isArray(data)
        ? data
        : [];

      const jefesProcesados = lista
        .map((jefe) => {
          const nombreJefe = [
            jefe.nombre1Jefe,
            jefe.nombre2Jefe,
            jefe.apellido1Jefe,
            jefe.apellido2Jefe,
          ]
            .filter(Boolean)
            .join(" ")
            .trim();

          return {
            ...jefe,
            documentoJefe: String(jefe.documentoJefe || ""),
            nombreJefe: nombreJefe || "Sin nombre registrado",
          };
        })
        .filter((jefe) => jefe.documentoJefe);

      setJefes(jefesProcesados);
    } catch (error) {
      console.error("Error al cargar los jefes:", error);

      if (manejarErrorSesion(error)) return;

      setJefes([]);
    } finally {
      setLoading(false);
    }
  }, [puedeLeer, manejarErrorSesion]);

  useEffect(() => {
    cargarJefes();
  }, [cargarJefes]);

  const fetchAsociacion = useCallback(async () => {
    if (!idasociado) return;

    if (!puedeLeer) {
      cleanData();
      return;
    }

    try {
      setLoading(true);

      const response = await axios.get(
        `/api/asociarjefes/listid/${idasociado}`,
        authConfig()
      );

      const data = response.data?.asociar_asignar;

      setEstadoAsignacion(data?.estadoAsignacion || "");

      const jefe = data?.jefeObjectId;

      if (jefe) {
        const nombreCompletoJefe = [
          jefe.nombre1Jefe,
          jefe.nombre2Jefe,
          jefe.apellido1Jefe,
          jefe.apellido2Jefe,
        ]
          .filter(Boolean)
          .join(" ")
          .trim();

        setJefeSeleccionado({
          value: String(jefe.documentoJefe || ""),
          label: `CC: ${jefe.documentoJefe || ""} - ${nombreCompletoJefe}`,
        });
      }
    } catch (error) {
      console.error("Error al cargar la asociación:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire("Error", "No se pudo cargar la asociación.", "error");
    } finally {
      setLoading(false);
    }
  }, [idasociado, puedeLeer, cleanData, manejarErrorSesion]);

  useEffect(() => {
    if (idasociado) fetchAsociacion();
  }, [idasociado, fetchAsociacion]);

  const opcionesJefes = useMemo(
    () =>
      (jefes || [])
        .map((jefe) => ({
          value: jefe.documentoJefe,
          label: `CC: ${jefe.documentoJefe} - ${
            jefe.nombreJefe || "Sin nombre registrado"
          }`,
        }))
        .sort((a, b) =>
          a.label.localeCompare(b.label, "es", {
            sensitivity: "base",
          })
        ),
    [jefes]
  );

  const opcionesEstado = useMemo(
    () => [
      { value: "Activo", label: "Activo" },
      { value: "Inactivo", label: "Inactivo" },
    ],
    []
  );

  const saveAsignacion = async () => {
    if (!puedeCrear) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para registrar asociaciones.",
        "warning"
      );
      return;
    }

    if (!jefeSeleccionado || !estadoAsignacion) {
      Swal.fire({
        icon: "warning",
        title: "Campos incompletos",
        text: "Debe seleccionar un Jefe y un Estado.",
      });
      return;
    }

    try {
      setLoading(true);

      const newAsignacion = {
        documentoJefe: jefeSeleccionado.value,
        estadoAsignacion,
      };

      await axios.post(
        `/api/asociarjefes/add/${id}`,
        newAsignacion,
        authConfig()
      );

      await Swal.fire({
        icon: "success",
        title: "La asociación ha sido registrada con éxito",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData();
      navigate("/enteconformador/listar");
    } catch (error) {
      if (manejarErrorSesion(error)) return;

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al guardar la asignación",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateAsignacion = async () => {
    if (!puedeActualizar) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para actualizar asociaciones.",
        "warning"
      );
      return;
    }

    if (!puedeLeer) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para consultar asociaciones.",
        "warning"
      );
      return;
    }

    if (!jefeSeleccionado || !estadoAsignacion) {
      Swal.fire(
        "Campos incompletos",
        "Debe seleccionar un Jefe y un Estado.",
        "warning"
      );
      return;
    }

    try {
      setLoading(true);

      const actualizacion = {
        documentoJefe: jefeSeleccionado.value,
        estadoAsignacion,
      };

      await axios.put(
        `/api/asociarjefes/update/${idasociado}`,
        actualizacion,
        authConfig()
      );

      await Swal.fire({
        icon: "success",
        title: "La asociación fue actualizada con éxito",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData();
      navigate("/enteconformador/ente/");
    } catch (error) {
      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message || "Error al actualizar la asignación",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const actions = async (e) => {
    e.preventDefault();

    if (loading) return;

    isEdit ? await updateAsignacion() : await saveAsignacion();
  };

  const handleCancelar = () => {
    cleanData();
    navigate(-1);
  };

  if (isEdit && !puedeLeer) {
    return (
      <div className="bg-body-tertiary py-4">
        <div className="container-fluid px-2 px-md-4">
          <div className="bg-white border rounded-4 shadow-sm p-4">
            <div className="text-center text-secondary py-5">
              <div className="display-6 mb-3">
                <i className="fa-solid fa-lock"></i>
              </div>

              <h5 className="fw-semibold text-dark">Sin permiso de lectura</h5>

              <p className="mb-4">
                No tienes permisos para consultar asociaciones.
              </p>

              <button
                className="btn btn-outline-dark"
                type="button"
                onClick={() => navigate(-1)}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isEdit && !puedeCrear) {
    return (
      <div className="bg-body-tertiary py-4">
        <div className="container-fluid px-2 px-md-4">
          <div className="bg-white border rounded-4 shadow-sm p-4">
            <div className="text-center text-secondary py-5">
              <div className="display-6 mb-3">
                <i className="fa-solid fa-lock"></i>
              </div>

              <h5 className="fw-semibold text-dark">Sin permiso de creación</h5>

              <p className="mb-4">
                No tienes permisos para registrar asociaciones.
              </p>

              <button
                className="btn btn-outline-dark"
                type="button"
                onClick={() => navigate(-1)}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isEdit && !puedeActualizar) {
    return (
      <div className="bg-body-tertiary py-4">
        <div className="container-fluid px-2 px-md-4">
          <div className="bg-white border rounded-4 shadow-sm p-4">
            <div className="text-center text-secondary py-5">
              <div className="display-6 mb-3">
                <i className="fa-solid fa-lock"></i>
              </div>

              <h5 className="fw-semibold text-dark">
                Sin permiso de actualización
              </h5>

              <p className="mb-4">
                No tienes permisos para editar asociaciones.
              </p>

              <button
                className="btn btn-outline-dark"
                type="button"
                onClick={() => navigate(-1)}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {isEdit
                    ? "Editar Asociación"
                    : "Asociar Jefe a Ente Conformador"}
                </h5>

                <small className="text-secondary">
                  Selecciona el jefe inmediato y define el estado de asignación.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={cargarJefes}
                  disabled={loading || !puedeLeer}
                >
                  <i
                    className={`fa-solid ${
                      loading ? "fa-spinner fa-spin" : "fa-rotate"
                    } me-2`}
                  ></i>
                  Cargar jefes
                </button>

                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" />
                <div className="mt-2">Procesando…</div>
              </div>
            )}

            <form id="form-asociacion" onSubmit={actions} noValidate>
              <div className="row g-3">
                <div className="col-12 col-md-7">
                  <label className="form-label fw-semibold">
                    Jefe Inmediato
                  </label>

                  {isEdit ? (
                    <input
                      type="text"
                      className="form-control"
                      value={jefeSeleccionado?.label || ""}
                      readOnly
                      disabled
                    />
                  ) : (
                    <Select
                      options={opcionesJefes}
                      value={jefeSeleccionado}
                      onChange={setJefeSeleccionado}
                      placeholder={
                        opcionesJefes.length > 0
                          ? "Seleccione un Jefe"
                          : "No hay jefes cargados"
                      }
                      noOptionsMessage={() => "No hay jefes disponibles"}
                      isClearable
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      isDisabled={loading || !puedeLeer}
                    />
                  )}
                </div>

                <div className="col-12 col-md-5">
                  <label className="form-label fw-semibold">
                    Estado de Asignación
                  </label>

                  <Select
                    options={opcionesEstado}
                    value={
                      opcionesEstado.find(
                        (op) => op.value === estadoAsignacion
                      ) || null
                    }
                    onChange={(opt) => setEstadoAsignacion(opt?.value || "")}
                    placeholder="Seleccione un Estado"
                    isClearable
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    isDisabled={loading}
                  />
                </div>

                <div className="col-12 pt-2">
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-dark"
                      onClick={cleanData}
                      disabled={loading}
                      title="Limpiar"
                    >
                      <i className="fa-solid fa-eraser me-2"></i>
                      Limpiar
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={handleCancelar}
                      disabled={loading}
                      title="Cancelar"
                    >
                      <i className="fa-solid fa-xmark me-2"></i>
                      Cancelar
                    </button>

                    {puedeGuardarFormulario && (
                      <button
                        type="submit"
                        form="form-asociacion"
                        className="btn btn-dark"
                        disabled={loading}
                        title={isEdit ? "Actualizar" : "Guardar"}
                      >
                        <i
                          className={`fa-solid ${
                            loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                          } me-2`}
                        ></i>

                        {loading
                          ? isEdit
                            ? "Actualizando…"
                            : "Guardando…"
                          : isEdit
                          ? "Actualizar"
                          : "Guardar"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>

            {!puedeGuardarFormulario && (
              <div className="alert alert-warning mt-3 mb-0">
                <i className="fa-solid fa-lock me-2"></i>
                No tienes permiso para {isEdit ? "actualizar" : "crear"}{" "}
                asociaciones.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarEnteVsJefe;